import React, { MouseEventHandler, TouchEventHandler } from 'react';
import { ConditionClassification } from '../../../interfaces/ProcessObject';
import styled from 'styled-components';
import { ButtonSmallText, ButtonRegularText, mq } from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { getConditionText } from '~/App/views/ObjectPage/helpers';
import { useIsDevice } from '../../../hooks/useIsDevice';
import {
  getConditionColors,
  getConditionIcon
} from '~/App/shared/helpers/conditionStatusHelpers';

type ConditionStatusStyleTypes = {
  $condition: ConditionClassification;
};

const Container = styled.div<ConditionStatusStyleTypes>`
  position: relative;
  border-radius: 6.25rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: ${({ $condition }) =>
    $condition === 'TESTED'
      ? '0.5rem 0.75rem'
      : '0.5rem 0.75rem 0.5rem 2.625rem'};
  background-color: ${({ theme, $condition }) =>
    getConditionColors(theme.colors, $condition)?.backgroundColor};
  color: ${({ theme, $condition }) =>
    getConditionColors(theme.colors, $condition)?.textColor};

  ${mq('tablet')} {
    padding: ${({ $condition }) =>
      $condition === 'TESTED'
        ? '0.375rem 0.625rem'
        : '0.375rem 0.625rem 0.375rem 2rem'};
  }
`;

const IconContainer = styled.div<ConditionStatusStyleTypes>`
  border-radius: 50%;
  margin-right: 0.5rem;
  position: absolute;
  left: -1px;
  padding: 0.375rem;
  border: ${({ theme }) => `2px solid ${theme.colors.background.light}`};
  background-color: ${({ theme, $condition }) =>
    getConditionColors(theme.colors, $condition)?.iconBackgroundColor};

  ${mq('tablet')} {
    padding: 0.25rem;
  }

  svg {
    height: 1.25rem;
    width: 1.25rem;

    ${mq('tablet')} {
      height: 1rem;
      width: 1rem;
    }
  }
`;

export const CardConditionStatus = ({
  condition,
  onClick,
  onEnter,
  onLeave,
  onTouch
}: {
  condition: ConditionClassification;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onEnter?: MouseEventHandler<HTMLDivElement>;
  onLeave?: MouseEventHandler<HTMLDivElement>;
  onTouch?: TouchEventHandler<HTMLDivElement>;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsDevice();

  return (
    <Container
      $condition={condition}
      onClick={onClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onTouchEnd={onTouch}
    >
      {condition !== 'TESTED' && (
        <IconContainer $condition={condition}>
          {getConditionIcon(condition)}
        </IconContainer>
      )}
      {isMobile ? (
        <ButtonRegularText>{getConditionText(condition, t)}</ButtonRegularText>
      ) : (
        <ButtonSmallText>{getConditionText(condition, t)}</ButtonSmallText>
      )}
    </Container>
  );
};
